import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw, Eye, Plug, Loader2, CheckCircle, XCircle, Database } from "lucide-react";
import { format } from "date-fns";

interface ConnectionForm {
  name: string;
  host: string;
  port: number;
  database_name: string;
  username: string;
  password: string;
  table_name: string;
  db_type: string;
  sync_target: string;
}

const defaultForm: ConnectionForm = {
  name: "",
  host: "",
  port: 5432,
  database_name: "",
  username: "",
  password: "",
  table_name: "transfer_bookings",
  db_type: "postgres",
  sync_target: "bookings",
};

export function ExternalConnectionsSection() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ConnectionForm>(defaultForm);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);

  const { data: connections = [], isLoading } = useQuery({
    queryKey: ["external_connections"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("external_connections")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: syncLogs = [] } = useQuery({
    queryKey: ["sync_logs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sync_logs")
        .select("*, external_connections(name)")
        .order("started_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data;
    },
  });

  const addConnection = useMutation({
    mutationFn: async (formData: ConnectionForm) => {
      const { error } = await supabase.from("external_connections").insert(formData);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["external_connections"] });
      setForm(defaultForm);
      setDialogOpen(false);
      toast.success("Conexión agregada");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const deleteConnection = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("external_connections").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["external_connections"] });
      queryClient.invalidateQueries({ queryKey: ["sync_logs"] });
      toast.success("Conexión eliminada");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const testConnection = async (conn: any) => {
    setTestingId(conn.id);
    try {
      const { data, error } = await supabase.functions.invoke("test-external-connection", {
        body: {
          host: conn.host,
          port: conn.port,
          database_name: conn.database_name,
          username: conn.username,
          password: conn.password,
          db_type: conn.db_type,
        },
      });
      if (error) throw error;
      if (data.success) {
        toast.success("✅ Conexión exitosa");
      } else {
        toast.error(`❌ ${data.error}`);
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setTestingId(null);
    }
  };

  const previewColumns = async (conn: any) => {
    setPreviewingId(conn.id);
    try {
      const { data, error } = await supabase.functions.invoke("preview-external-columns", {
        body: {
          host: conn.host,
          port: conn.port,
          database_name: conn.database_name,
          username: conn.username,
          password: conn.password,
          table_name: conn.table_name,
          db_type: conn.db_type,
        },
      });
      if (error) throw error;
      if (data.success) {
        setPreviewData(data);
        setPreviewOpen(true);
      } else {
        toast.error(data.error);
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setPreviewingId(null);
    }
  };

  const syncData = async (conn: any) => {
    setSyncingId(conn.id);
    try {
      const functionName = conn.sync_target === "tour_bookings" ? "sync-external-tours" : "sync-external-data";
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: { connection_id: conn.id },
      });
      if (error) throw error;
      if (data.success) {
        toast.success(`✅ Sincronización completada: ${data.records_added} nuevos, ${data.records_updated} actualizados`);
        queryClient.invalidateQueries({ queryKey: ["sync_logs"] });
        queryClient.invalidateQueries({ queryKey: ["external_connections"] });
      } else {
        toast.error(`❌ ${data.error}`);
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Conexiones Externas</h2>
          <p className="text-sm text-muted-foreground">
            Conecta bases de datos externas para importar reservas automáticamente
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" /> Agregar Conexión
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nueva Conexión Externa</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nombre</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Shuttle Adventures DB" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Host</Label>
                  <Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="db.example.supabase.co" />
                </div>
                <div>
                  <Label>Puerto</Label>
                  <Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} />
                </div>
              </div>
              <div>
                <Label>Base de Datos</Label>
                <Input value={form.database_name} onChange={(e) => setForm({ ...form, database_name: e.target.value })} placeholder="postgres" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Usuario</Label>
                  <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="postgres" />
                </div>
                <div>
                  <Label>Contraseña</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Tabla</Label>
                  <Input value={form.table_name} onChange={(e) => setForm({ ...form, table_name: e.target.value })} />
                </div>
                <div>
                  <Label>Tipo DB</Label>
                  <Select value={form.db_type} onValueChange={(v) => setForm({ ...form, db_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="postgres">PostgreSQL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Destino Sync</Label>
                  <Select value={form.sync_target} onValueChange={(v) => setForm({ ...form, sync_target: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bookings">Transfers</SelectItem>
                      <SelectItem value="tour_bookings">Tours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button className="w-full" onClick={() => addConnection.mutate(form)} disabled={addConnection.isPending}>
                {addConnection.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Guardar Conexión
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Connections cards */}
      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : connections.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Database className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No hay conexiones configuradas</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {connections.map((conn: any) => (
            <Card key={conn.id}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{conn.name}</CardTitle>
                    <CardDescription>
                      {conn.host}:{conn.port} / {conn.database_name} → <code className="text-xs">{conn.table_name}</code>
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={conn.is_active ? "default" : "secondary"}>
                      {conn.is_active ? "Activa" : "Inactiva"}
                    </Badge>
                    <Badge variant="outline">
                      {conn.sync_target === "tour_bookings" ? "Tours" : "Transfers"}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => testConnection(conn)} disabled={testingId === conn.id}>
                    {testingId === conn.id ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plug className="h-4 w-4 mr-1" />}
                    Test
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => previewColumns(conn)} disabled={previewingId === conn.id}>
                    {previewingId === conn.id ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                    Preview
                  </Button>
                  <Button size="sm" onClick={() => syncData(conn)} disabled={syncingId === conn.id}>
                    {syncingId === conn.id ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                    Sync Now
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => deleteConnection.mutate(conn.id)}>
                    <Trash2 className="h-4 w-4 mr-1" /> Eliminar
                  </Button>
                </div>
                {conn.last_synced_at && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Última sincronización: {format(new Date(conn.last_synced_at), "dd/MM/yyyy HH:mm")}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Vista Previa de Tabla Externa</DialogTitle>
          </DialogHeader>
          {previewData && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Total registros: <strong>{previewData.total_records}</strong></p>
              <div>
                <h4 className="font-semibold mb-2">Columnas</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Columna</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Nullable</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.columns?.map((col: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{col.column_name}</TableCell>
                        <TableCell className="text-xs">{col.data_type}</TableCell>
                        <TableCell>{col.is_nullable === "YES" ? "Sí" : "No"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {previewData.sample?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Muestra de datos</h4>
                  <div className="overflow-auto">
                    <pre className="text-xs bg-muted p-3 rounded-md">
                      {JSON.stringify(previewData.sample, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sync History */}
      {syncLogs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Historial de Sincronización</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Conexión</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Agregados</TableHead>
                  <TableHead>Actualizados</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {syncLogs.map((log: any) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm">{(log.external_connections as any)?.name || "—"}</TableCell>
                    <TableCell>
                      {log.status === "completed" && <Badge variant="default" className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />OK</Badge>}
                      {log.status === "error" && <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Error</Badge>}
                      {log.status === "running" && <Badge variant="secondary"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Running</Badge>}
                    </TableCell>
                    <TableCell>{log.records_added}</TableCell>
                    <TableCell>{log.records_updated}</TableCell>
                    <TableCell className="text-xs">{format(new Date(log.started_at), "dd/MM/yyyy HH:mm")}</TableCell>
                    <TableCell className="text-xs text-destructive max-w-[200px] truncate">{log.error_message || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
